import { Country, State } from 'country-state-city';

/* Which of a member's country, state and mobile are still missing.
 *
 * The QR sign-up (`features/GiveawayOffer/GiveawayOfferPage`) no longer asks for
 * any of the three (2026-10-07); the server stores them EMPTY for such an account
 * and the portal holds the member in `ProfileDetailsModal` until they are saved.
 * This is the one rule both sides of that dialog read — whether to show it, and
 * what it must insist on — so it lives here and nowhere else.
 *
 * A state is required only where the country HAS states, as `country-state-city`
 * knows them: a member in Singapore has nothing to pick and must not be locked out
 * for it. The stored country is a NAME (the sign-up and the dialog both send the
 * country's name) but older rows may hold an ISO code; both are matched, and a
 * country we cannot match at all is taken at its word — the dialog is for members
 * missing a detail, not for correcting ones that are there.
 */
export type ProfileDetailField =
  | 'username'
  | 'firstName'
  | 'lastName'
  | 'country'
  | 'state'
  | 'phone'
  | 'giveawayEligibility';

export type ProfileDetails = {
  phone?: string | null;
  country?: string | null;
  state?: string | null;
  /* A partner's member (Empire, 2026-10-21) arrives with no username and no
     names — the partner's display name is not stored — and the dialog asks. */
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  /* null = never answered (the dialog asks); true/false = answered */
  isGiveawayEligible?: boolean | null;
};

const clean = (v: unknown) => String(v ?? '').trim();

/** The ISO-2 code of a stored country value (a name or a code), or null. */
export const findCountryIso2 = (country?: string | null): string | null => {
  const wanted = clean(country).toLowerCase();
  if (!wanted) return null;
  const match = Country.getAllCountries().find(
    (c) => c.isoCode.toLowerCase() === wanted || c.name.toLowerCase() === wanted
  );
  return match ? match.isoCode : null;
};

export const countryHasStates = (iso2: string | null) =>
  Boolean(iso2) && (State.getStatesOfCountry(iso2 as string) || []).length > 0;

export const missingProfileDetails = (
  user: ProfileDetails | null | undefined
): ProfileDetailField[] => {
  if (!user) return [];
  const missing: ProfileDetailField[] = [];
  if (!clean(user.username)) missing.push('username');
  if (!clean(user.firstName)) missing.push('firstName');
  if (!clean(user.lastName)) missing.push('lastName');
  const country = clean(user.country);
  if (!country) missing.push('country');
  /* No country yet → a state will be asked for once one is picked (if that
     country has any); the dialog is open either way, so nothing is lost by
     listing it. A known country without states → no state owed. */
  const iso2 = findCountryIso2(country);
  const stateOwed = !country || countryHasStates(iso2);
  if (stateOwed && !clean(user.state)) missing.push('state');
  if (!clean(user.phone)) missing.push('phone');
  if (user.isGiveawayEligible === null) missing.push('giveawayEligibility');
  return missing;
};

export const isProfileIncomplete = (user: ProfileDetails | null | undefined) =>
  missingProfileDetails(user).length > 0;
